import React from 'react';
import { Card, Col, Row, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  OiEmptyState,
  OiPageFrame,
  PrimaryKpiCard,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { intelligenceService } from '@/services/intelligence.service';

const { Title, Paragraph } = Typography;

const ModuleIntelligencePage: React.FC = () => {
  const healthQ = useQuery({
    queryKey: ['eibm-module-health'],
    queryFn: () => intelligenceService.getModuleHealth(),
    staleTime: 60_000,
  });

  const rows = (healthQ.data || []) as Array<{
    module_code: string;
    health_score: number;
    computed_at: string;
  }>;

  return (
    <OiPageFrame subtitle="Adoption and health of embedded Enterprise Intelligent Business Modules (EIBM).">
      <Title level={3} style={{ marginTop: 0 }}>
        Module Intelligence
      </Title>
      <Paragraph type="secondary">
        Contextual intelligence lives in the Universal Intelligence Sidebar on transactional pages
        (Ctrl+Shift+I). This page shows module health scores only — it does not replace ERP screens.
      </Paragraph>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={8}>
          <PrimaryKpiCard label="Modules tracked" value={rows.length} />
        </Col>
        <Col xs={24} md={8}>
          <PrimaryKpiCard
            label="Avg health"
            value={
              rows.length
                ? Math.round(rows.reduce((s, r) => s + Number(r.health_score || 0), 0) / rows.length)
                : '—'
            }
          />
        </Col>
        <Col xs={24} md={8}>
          <PrimaryKpiCard label="Sidebar" value="Embedded" />
        </Col>
      </Row>

      <Card title="Module health">
        {healthQ.isError ? (
          <OiEmptyState title="Health unavailable" body="Soft-fail: try again after context packs run." />
        ) : (
          <Table
            rowKey={(r) => r.module_code}
            loading={healthQ.isLoading}
            dataSource={rows}
            pagination={false}
            columns={[
              { title: 'Module', dataIndex: 'module_code' },
              {
                title: 'Health',
                dataIndex: 'health_score',
                render: (v: number) => {
                  const n = Math.round(Number(v) || 0);
                  return <Tag color={n >= 70 ? 'green' : n >= 50 ? 'gold' : 'red'}>{n}</Tag>;
                },
              },
              { title: 'Computed', dataIndex: 'computed_at' },
            ]}
          />
        )}
      </Card>
    </OiPageFrame>
  );
};

export default ModuleIntelligencePage;
